// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, useTheme } from '@mui/system';
import { Alert, Button, Divider, Grid, IconButton, List, ListItem, ListItemIcon, Paper, Snackbar, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import { UpdateCustomBundleThunk } from 'Features/bundle/bundleThunk';
import { useAppDispatch } from 'Hooks';
import { dollarsPerFoot } from 'Utils/numberFormat';
import stylesDef from './styles';

interface CustomBundleItemProps {
  bundle: ICustomBundle;
  handleOpenDeleteDialog: (bundleId: string) => void;
}

const CustomBundleItem = ({ bundle, handleOpenDeleteDialog }: CustomBundleItemProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const [showMore, setShowMore] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(bundle.name);
  const [showError, setShowError] = useState(false);
  const [failedResultMessage, setFailedResultMessage] = useState('');
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [editedPricePerFoot, setEditedPricePerFoot] = useState(String(bundle.pricePerFoot ?? 0));

  const handleSave = async () => {
    const saveResult = await dispatch(UpdateCustomBundleThunk(bundle._id, editedName));
    if (saveResult.success) {
      setIsEditing(false);
    } else {
      setIsEditing(false);
      setEditedName(bundle.name);
      setShowError(true);
      setFailedResultMessage(saveResult.message);
    }
  };

  // Admin-set contracted price per foot ($/ft). Saving via UpdateCustomBundleThunk marks the bundle
  // admin-set server-side (AUT-6265); the row re-renders from the response, no reload.
  const handleSavePrice = async () => {
    const rate = Number(editedPricePerFoot);
    if (!Number.isFinite(rate) || rate < 0) {
      setIsEditingPrice(false);
      setEditedPricePerFoot(String(bundle.pricePerFoot ?? 0));
      setShowError(true);
      setFailedResultMessage('Price per foot must be a non-negative number');
      return;
    }
    const saveResult = await dispatch(UpdateCustomBundleThunk(bundle._id, bundle.name, rate));
    if (saveResult.success) {
      setIsEditingPrice(false);
    } else {
      setIsEditingPrice(false);
      setEditedPricePerFoot(String(bundle.pricePerFoot ?? 0));
      setShowError(true);
      setFailedResultMessage(saveResult.message);
    }
  };

  return (
    <Paper elevation={1} sx={styles.paper}>
      <Snackbar open={showError} autoHideDuration={5000} onClose={() => setShowError(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" sx={styles.error} color="error">{failedResultMessage}</Alert>
      </Snackbar>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Box sx={styles.content}>
            {isEditing ? (
              <Box sx={styles.flexCenter}>
                <TextField
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  size="small"
                  variant="standard"
                  sx={styles.textField}
                />
                <IconButton onClick={handleSave}>
                  <CheckIcon sx={styles.finishedIcon} />
                </IconButton>
              </Box>
            ) : (
              <Box sx={styles.flexCenter}>
                <Typography variant="h4" sx={styles.bundleTitle}>
                  {`${bundle.name} ${bundle.includeAttribute ? '(W/ ATTRIBUTES)' : ''}`}
                </Typography>
                <IconButton onClick={() => setIsEditing(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            {
              bundle.createdAt && (
                <Typography variant="body1" sx={styles.bundleDate}>
                  Created on:
                  {' '}
                  {new Date(bundle.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Typography>
              )
            }
          </Box>
        </Grid>
        <Grid item xs={1}>
          {/* layers */}
          <Box sx={styles.contentList}>
            <Typography sx={styles.contentCounter}>{bundle.layers.length}</Typography>
            <Typography sx={styles.contentCounterText}>Layers</Typography>
          </Box>
        </Grid>
        <Grid item xs={2}>
          {/* deliverables */}
          <Box sx={styles.contentList}>
            <Typography sx={styles.contentCounter}>{bundle.deliverables.length}</Typography>
            <Typography sx={styles.contentCounterText}>Deliverables</Typography>
          </Box>
        </Grid>
        <Grid item xs={2}>
          {/* Price Per Foot — admin-editable contracted rate (AUT-6265) */}
          <Box sx={styles.contentList}>
            {isEditingPrice ? (
              <Box sx={styles.flexCenter}>
                <TextField
                  value={editedPricePerFoot}
                  onChange={(e) => setEditedPricePerFoot(e.target.value)}
                  size="small"
                  variant="standard"
                  type="number"
                  inputProps={{ min: 0, step: 0.0001 }}
                  sx={styles.textField}
                />
                <IconButton onClick={handleSavePrice}>
                  <CheckIcon sx={styles.finishedIcon} />
                </IconButton>
              </Box>
            ) : (
              <Box sx={styles.flexCenter}>
                <Typography sx={styles.contentCounter}>{dollarsPerFoot(bundle.pricePerFoot ?? 0)}</Typography>
                <IconButton onClick={() => { setEditedPricePerFoot(String(bundle.pricePerFoot ?? 0)); setIsEditingPrice(true); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Typography sx={styles.contentCounterText}>Price Per Foot</Typography>
          </Box>
        </Grid>
        <Grid item xs={2}>
          <Button onClick={() => setShowMore(!showMore)}>{showMore ? 'HIDE LAYERS' : 'SHOW LAYERS'}</Button>
        </Grid>
        <Grid item xs={1}>
          <IconButton onClick={() => handleOpenDeleteDialog(bundle._id)}>
            <DeleteIcon sx={styles.deleteIcon} />
          </IconButton>
        </Grid>
      </Grid>

      {
        showMore && (
          <>
            <Divider sx={styles.divider} />
            <Box sx={styles.expandedContent}>
              <Box sx={styles.expandedColumn}>
                <Typography variant="h5">Layers</Typography>
                <List>
                  {
                    bundle.layers.map((layer: ICustomBundleAddon) => (
                      <ListItem sx={styles.bundleListItem} key={layer._id}>
                        <ListItemIcon sx={styles.listItemIcon}>
                          <CheckIcon sx={styles.finishedIcon} />
                        </ListItemIcon>
                        <Typography>
                          {layer.name}
                          {' '}
                          {layer.is3D ? '(3D)' : ''}
                        </Typography>
                      </ListItem>
                    ))
                  }
                </List>
              </Box>
              <Box sx={styles.expandedColumn}>
                <Typography variant="h5">Deliverables</Typography>
                <List>
                  {
                    bundle.deliverables.map((deliverable: ICustomBundleAddon) => (
                      <ListItem sx={styles.bundleListItem} key={deliverable._id}>
                        <ListItemIcon sx={styles.listItemIcon}>
                          <CheckIcon sx={styles.finishedIcon} />
                        </ListItemIcon>
                        <Typography>{deliverable.name}</Typography>
                      </ListItem>
                    ))
                  }
                </List>
              </Box>
            </Box>
          </>
        )
      }
    </Paper>
  );
};

export default CustomBundleItem;
